import api from './api';

export enum VendorCategory {
  RESTAURANT = 'RESTAURANT',
  FAST_FOOD = 'FAST_FOOD',
  CAFE = 'CAFE',
  BAKERY = 'BAKERY',
  SUPERMARKET = 'SUPERMARKET',
  GROCERY_STORE = 'GROCERY_STORE',
  PHARMACY = 'PHARMACY',
  BOOKSTORE = 'BOOKSTORE',
  STATIONERY = 'STATIONERY',
  ELECTRONICS = 'ELECTRONICS',
  CLOTHING = 'CLOTHING',
  BARBERSHOP = 'BARBERSHOP',
  SALON = 'SALON',
  OTHER = 'OTHER',
}

export interface Vendor {
  id: string;
  name: string;
  description: string;
  category: VendorCategory;
  images: string[];
  address: string;
  city: string;
  state: string;
  zipCode: string;
  latitude: number;
  longitude: number;
  universityId?: string;
  phoneNumber: string;
  email?: string;
  website?: string;
  openingHours?: Record<string, string>;
  deliveryAvailable: boolean;
  deliveryFee?: number;
  deliveryRadiusKm?: number | null;
  minimumOrder?: number;
  averageRating?: number;
  totalReviews: number;
  isActive: boolean;
  isVerified: boolean;
  ownerId: string;
  createdAt: string;
  updatedAt: string;
  // Verification fields
  businessLicense?: string;
  idDocument?: string;
  taxIdNumber?: string;
  registrationNumber?: string;
  verificationStatus?: string;
  verificationNotes?: string;
  verifiedAt?: string;
  verifiedBy?: string;
  // Featured placement
  isFeatured: boolean;
  featuredUntil?: string | null;
  featuredBadge?: string | null;
  // Relations
  university?: {
    id: string;
    name: string;
    latitude: number;
    longitude: number;
  };
  owner?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber?: string;
    idVerified?: boolean;
    idDocumentType?: 'GHANA_CARD' | 'PASSPORT' | 'NON_RESIDENT_GHANA_CARD' | null;
    phoneVerified?: boolean;
    emailVerified?: boolean;
  };
  menuItems?: MenuItem[];
  reviews?: VendorReview[];
  _count?: {
    menuItems: number;
    orders: number;
    reviews: number;
    favorites: number;
  };
}

export interface MenuItem {
  id: string;
  vendorId: string;
  name: string;
  description?: string;
  price: number;
  image?: string;
  category?: string;
  isAvailable: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface VendorFilters {
  category?: VendorCategory;
  universityId?: string;
  ownerId?: string;
  city?: string;
  minLat?: number;
  maxLat?: number;
  minLng?: number;
  maxLng?: number;
  search?: string;
  isActive?: boolean;
  featured?: boolean;
}

export interface VendorPagination {
  page?: number;
  limit?: number;
}

export interface OrderItem {
  menuItemId: string;
  quantity: number;
}

export interface CreateOrderData {
  vendorId: string;
  items: OrderItem[];
  deliveryAddress?: string;
  deliveryInstructions?: string;
  paymentMethod?: 'PAYSTACK_CARD' | 'PAYSTACK_BANK' | 'PAYSTACK_USSD';
  recipientNumber?: string;
  momoDetails?: {
    network?: string;
    phoneNumber?: string;
  };
  referralTokensToSpend?: number;
  // Guest checkout
  guestInfo?: {
    name: string;
    phoneNumber: string;
    email: string;
  };
  verificationCode?: string;
  verificationMethod?: 'phone' | 'email';
}

export interface Order {
  id: string;
  vendorId: string;
  userId?: string;
  status: string;
  totalAmount: number;
  deliveryAddress?: string;
  deliveryInstructions?: string;
  items: Array<{
    id: string;
    menuItemId: string;
    quantity: number;
    price: number;
    menuItem: MenuItem;
  }>;
  payment?: {
    id: string;
    status: string;
    amount: number;
    paymentMethod: string;
  };
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    buyerVerified?: boolean;
  };
  vendor?: Vendor;
  createdAt: string;
  updatedAt: string;
}

export interface VendorReview {
  id: string;
  vendorId: string;
  userId: string;
  orderId?: string;
  rating: number;
  comment: string;
  images: string[];
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    avatar?: string;
  };
}

// Get all vendors
export const getVendors = async (
  filters?: VendorFilters,
  pagination?: VendorPagination
): Promise<{ vendors: Vendor[]; pagination: any }> => {
  try {
    const params: any = {};
    
    if (filters) {
      if (filters.category) params.category = filters.category;
      if (filters.universityId) params.universityId = filters.universityId;
      if (filters.ownerId) params.ownerId = filters.ownerId;
      if (filters.city) params.city = filters.city;
      if (filters.minLat) params.minLat = filters.minLat;
      if (filters.maxLat) params.maxLat = filters.maxLat;
      if (filters.minLng) params.minLng = filters.minLng;
      if (filters.maxLng) params.maxLng = filters.maxLng;
      if (filters.search) params.search = filters.search;
      if (filters.isActive !== undefined) params.isActive = filters.isActive;
      if (filters.featured !== undefined) params.featured = filters.featured;
    }
    
    if (pagination) {
      if (pagination.page) params.page = pagination.page;
      if (pagination.limit) params.limit = pagination.limit;
    }
    
    const response = await api.get('/vendors', { params });
    
    if (Array.isArray(response.data)) {
      return { vendors: response.data, pagination: {} };
    }
    
    return {
      vendors: response.data.vendors || [],
      pagination: response.data.pagination || {},
    };
  } catch (error: any) {
    console.error('[VendorService] Failed to fetch vendors:', error);
    throw error;
  }
};

// Get vendor by ID
export const getVendorById = async (id: string): Promise<Vendor> => {
  try {
    const response = await api.get(`/vendors/${id}`);
    return response.data;
  } catch (error: any) {
    console.error('[VendorService] Failed to fetch vendor:', error);
    throw error;
  }
};

// Get vendor menu items
export const getVendorMenuItems = async (vendorId: string): Promise<MenuItem[]> => {
  try {
    const response = await api.get(`/vendors/${vendorId}/menu-items`);
    return response.data;
  } catch (error: any) {
    console.error('[VendorService] Failed to fetch menu items:', error);
    throw error;
  }
};

// Create vendor
export const createVendor = async (vendorData: Partial<Vendor>): Promise<Vendor> => {
  try {
    const response = await api.post('/vendors', vendorData);
    return response.data;
  } catch (error: any) {
    console.error('[VendorService] Failed to create vendor:', error);
    throw error;
  }
};

// Generate a shop description from the fields already filled in
export const generateVendorDescription = async (fields: Record<string, any>): Promise<{ ok: boolean; description?: string }> => {
  try {
    const response = await api.post('/ai/generate-description', { type: 'vendor', fields });
    return response.data.data;
  } catch (error: any) {
    console.error('[VendorService] Failed to generate description:', error);
    throw error;
  }
};

// Update vendor
export const updateVendor = async (id: string, vendorData: Partial<Vendor>): Promise<Vendor> => {
  try {
    const response = await api.put(`/vendors/${id}`, vendorData);
    return response.data;
  } catch (error: any) {
    console.error('[VendorService] Failed to update vendor:', error);
    throw error;
  }
};

// Delete vendor
export const deleteVendor = async (id: string): Promise<void> => {
  try {
    await api.delete(`/vendors/${id}`);
  } catch (error: any) {
    console.error('[VendorService] Failed to delete vendor:', error);
    throw error;
  }
};

// Create menu item
export const createMenuItem = async (vendorId: string, menuItemData: Partial<MenuItem>): Promise<MenuItem> => {
  try {
    const response = await api.post(`/vendors/${vendorId}/menu-items`, menuItemData);
    return response.data;
  } catch (error: any) {
    console.error('[VendorService] Failed to create menu item:', error);
    throw error;
  }
};

// Update menu item
export const updateMenuItem = async (vendorId: string, itemId: string, menuItemData: Partial<MenuItem>): Promise<MenuItem> => {
  try {
    const response = await api.put(`/vendors/${vendorId}/menu-items/${itemId}`, menuItemData);
    return response.data;
  } catch (error: any) {
    console.error('[VendorService] Failed to update menu item:', error);
    throw error;
  }
};

// Delete menu item
export const deleteMenuItem = async (vendorId: string, itemId: string): Promise<void> => {
  try {
    await api.delete(`/vendors/${vendorId}/menu-items/${itemId}`);
  } catch (error: any) {
    console.error('[VendorService] Failed to delete menu item:', error);
    throw error;
  }
};

// Get vendor analytics
export const getVendorAnalytics = async (vendorId: string): Promise<any> => {
  try {
    const response = await api.get(`/vendors/${vendorId}/analytics`);
    return response.data;
  } catch (error: any) {
    console.error('[VendorService] Failed to fetch analytics:', error);
    throw error;
  }
};

// Create order
export const createOrder = async (orderData: CreateOrderData): Promise<{ order: Order; paymentUrl?: string }> => {
  try {
    const response = await api.post('/vendors/orders', orderData);
    return response.data;
  } catch (error: any) {
    console.error('[VendorService] Failed to create order:', error);
    throw error;
  }
};

// Get orders
export const getOrders = async (filters?: { vendorId?: string; userId?: string }, token?: string): Promise<Order[]> => {
  try {
    const params: any = {};
    if (filters?.vendorId) params.vendorId = filters.vendorId;
    if (filters?.userId) params.userId = filters.userId;
    
    const response = await api.get('/vendors/orders', { params });
    return response.data.orders || response.data || [];
  } catch (error: any) {
    console.error('[VendorService] Failed to fetch orders:', error);
    throw error;
  }
};

// Get order by ID
export const getOrderById = async (orderId: string): Promise<Order> => {
  try {
    const response = await api.get(`/vendors/orders/${orderId}`);
    return response.data;
  } catch (error: any) {
    console.error('[VendorService] Failed to fetch order:', error);
    throw error;
  }
};

// Update order status
export const updateOrderStatus = async (orderId: string, status: { status: string }, token?: string): Promise<Order> => {
  try {
    const response = await api.put(`/vendors/orders/${orderId}/status`, status);
    return response.data;
  } catch (error: any) {
    console.error('[VendorService] Failed to update order status:', error);
    throw error;
  }
};

// Cancel order
export const cancelOrder = async (orderId: string): Promise<void> => {
  try {
    await api.put(`/vendors/orders/${orderId}/cancel`);
  } catch (error: any) {
    console.error('[VendorService] Failed to cancel order:', error);
    throw error;
  }
};

// Get vendor reviews
export const getVendorReviews = async (vendorId: string, options?: { page?: number; limit?: number }): Promise<{ reviews: VendorReview[]; pagination: any }> => {
  try {
    const params: any = {};
    if (options?.page) params.page = options.page;
    if (options?.limit) params.limit = options.limit;
    
    const response = await api.get(`/vendors/${vendorId}/reviews`, { params });
    return {
      reviews: response.data.reviews || response.data || [],
      pagination: response.data.pagination || {},
    };
  } catch (error: any) {
    console.error('[VendorService] Failed to fetch reviews:', error);
    throw error;
  }
};

// Create review
export const createReview = async (reviewData: {
  vendorId: string;
  orderId?: string;
  rating: number;
  comment: string;
  images?: string[];
}): Promise<VendorReview> => {
  try {
    const response = await api.post('/vendors/reviews', reviewData);
    return response.data;
  } catch (error: any) {
    console.error('[VendorService] Failed to create review:', error);
    throw error;
  }
};

// Add to favorites
export const addFavorite = async (vendorId: string): Promise<void> => {
  try {
    await api.post('/vendors/favorites', { vendorId });
  } catch (error: any) {
    console.error('[VendorService] Failed to add favorite:', error);
    throw error;
  }
};

// Remove from favorites
export const removeFavorite = async (vendorId: string): Promise<void> => {
  try {
    await api.delete(`/vendors/favorites/${vendorId}`);
  } catch (error: any) {
    console.error('[VendorService] Failed to remove favorite:', error);
    throw error;
  }
};

// Get user favorites
export const getUserFavorites = async (): Promise<Vendor[]> => {
  try {
    const response = await api.get('/vendors/favorites');
    return response.data.vendors || response.data || [];
  } catch (error: any) {
    console.error('[VendorService] Failed to fetch favorites:', error);
    throw error;
  }
};

// Guest order verification
export const sendGuestOrderVerification = async (contact: string, method: 'phone' | 'email'): Promise<{ success: boolean; message: string; code?: string }> => {
  try {
    const endpoint = method === 'phone' ? '/vendors/orders/verify-phone' : '/vendors/orders/verify-email';
    const response = await api.post(endpoint, method === 'phone' ? { phoneNumber: contact } : { email: contact });
    return response.data;
  } catch (error: any) {
    console.error('[VendorService] Failed to send verification:', error);
    throw error;
  }
};

// Admin: Get pending vendor verifications
export const getPendingVendorVerifications = async (): Promise<Vendor[]> => {
  try {
    const response = await api.get('/vendors/admin/pending-verifications');
    return response.data.vendors || [];
  } catch (error: any) {
    console.error('[VendorService] Failed to fetch pending verifications:', error);
    throw error;
  }
};

// Admin: Verify vendor
export const verifyVendor = async (vendorId: string, review: { status: 'APPROVED' | 'REJECTED'; notes?: string }): Promise<Vendor> => {
  try {
    const response = await api.put(`/vendors/admin/${vendorId}/verify`, review);
    return response.data;
  } catch (error: any) {
    console.error('[VendorService] Failed to verify vendor:', error);
    throw error;
  }
};

// Admin: Get vendor metrics
export const getVendorMetrics = async (): Promise<any> => {
  try {
    const response = await api.get('/analytics/vendors');
    return response.data.data || {};
  } catch (error: any) {
    console.error('[VendorService] Failed to fetch vendor metrics:', error);
    throw error;
  }
};



